import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  CalendarClock,
  Clock,
  CornerLeftUp,
  History,
  Lock,
  Send,
  UserPlus,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MentionPicker } from '@/components/tasks/MentionAutocomplete';
import { useMentionInput } from '@/hooks/useMentionField';
import { MoveMenuItems } from '@/components/tasks/MoveActions';
import { TimeSection } from '@/components/tasks/TimeSection';
import { billingApi, projectsApi } from '@/lib/api/endpoints';
import { PriorityDot } from '@/components/tasks/PriorityDot';
import { useT, type MessageKey, type Params } from '@/lib/i18n';
import { PRIORITY_LABELS, PRIORITY_ORDER } from '@/lib/priority';
import { Attachments } from '@/components/tasks/Attachments';
import { DescriptionEditor } from '@/components/tasks/DescriptionEditor';
import { LabelPicker } from '@/components/tasks/LabelPicker';
import { CloseGuard } from '@/components/tasks/CloseGuard';
import { ParentPicker } from '@/components/tasks/ParentPicker';
import { Subtasks } from '@/components/tasks/Subtasks';
import { TaskCheckbox } from '@/components/tasks/TaskCheckbox';
import {
  HandoverBanner,
  HandoverDialog,
} from '@/components/tasks/HandoverDialog';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Markdown } from '@/components/ui/markdown';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuTrigger,
} from '@/components/ui/menu';
import { ErrorState, LoadingState } from '@/components/ui/states';
import {
  useAssignTask,
  useCompleteTask,
  useMoveTask,
  useReopenTask,
  useTask,
  useUpdateTask,
} from '@/hooks/useTasks';
import {
  kb,
  activity as activityApi,
  comments as commentsApi,
} from '@/lib/api/endpoints';
import type { Activity, TaskDetail, TaskPriority,
  FixedChargeState,
  HandoverKind,
} from '@/lib/api/types';
import { formatAmount } from '@/routes/Clients';
import { formatDayShort, formatTimestamp, toDayString } from '@/lib/dates';
import { keys } from '@/lib/queryClient';
import { cn } from '@/lib/utils';

/**
 * The task detail, as the right-hand pane from the macOS design.
 *
 * A panel rather than a modal: the list stays visible and interactive beside
 * it, which is what makes "open a step, glance at the parent row" work. On
 * narrow viewports it overlays the list instead — a third column at 380px on a
 * phone would crush both.
 */
export function TaskDetailPanel({
  taskId,
  currentUserId,
  onClose,
  onOpenTask,
}: {
  taskId: string | null;
  currentUserId: string;
  onClose: () => void;
  /** Opening a step swaps the panel to it, rather than stacking panels. */
  onOpenTask?: (taskId: string) => void;
}) {
  const t = useT();
  const { data: task, isPending, error, refetch } = useTask(taskId);

  if (!taskId) return null;

  return (
    <aside
      aria-label={task?.title ?? t('task.detail')}
      className={cn(
        // A layer above the list, so opening a task never reflows the screen
        // the person is reading — up to the point where the screen is wide
        // enough to hold both. From 1536px there is room for three real
        // columns, and covering the list there would be hiding something for
        // no reason; the panel joins the layout instead.
        'scroll-area overflow-y-auto border-l border-border bg-detail-pane',
        'absolute inset-y-0 right-0 z-30 shadow-2xl',
        '2xl:static 2xl:z-auto 2xl:shadow-none',
        // Full-bleed on a phone — at 375px a "panel" beside nothing is just a
        // cramped page — and a readable half of the window from `lg` up.
        'w-full min-w-0 lg:w-[min(55vw,820px)] lg:min-w-[400px] lg:max-w-full',
        // Nearly half the screen once it is a column. A fixed 34rem left a
        // gap of dead space between the list and the panel on a wide monitor —
        // the room was there, and neither side was using it.
        '2xl:w-[42vw] 2xl:min-w-[30rem] 2xl:max-w-[56rem] 2xl:shrink-0',
        // The slide-in belongs to the overlay; a column that is simply there
        // should not come flying in.
        'motion-safe:animate-panel-in 2xl:animate-none',
      )}
    >
      <div className="relative p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className={cn(
            'absolute right-4 top-4 rounded p-1.5 text-content-subtle',
            'transition-surface hover:bg-surface-hover hover:text-content',
            'max-sm:right-2 max-sm:top-2 max-sm:p-3 touch:right-2 touch:top-2 touch:p-3',
          )}
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        {isPending ? <LoadingState /> : null}
        {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}
        {task ? (
          <TaskDetailBody
            task={task}
            currentUserId={currentUserId}
            onClose={onClose}
            onOpenTask={onOpenTask}
          />
        ) : null}
      </div>
    </aside>
  );
}

/** The way up from a step: its parent, one click away. */
function ParentLink({
  task,
  onOpenTask,
}: {
  task: TaskDetail;
  onOpenTask?: (taskId: string) => void;
}) {
  const t = useT();
  if (!task.parent_task_id) return null;
  return (
    <button
      type="button"
      onClick={() => onOpenTask?.(task.parent_task_id!)}
      disabled={!onOpenTask}
      className="mb-1.5 flex max-w-full items-center gap-1 text-xs text-content-subtle transition-surface hover:text-accent disabled:cursor-default disabled:hover:text-content-subtle"
    >
      <CornerLeftUp className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{task.parent_title ?? t('task.openParent')}</span>
    </button>
  );
}

function TaskDetailBody({
  task,
  currentUserId,
  onClose,
  onOpenTask,
}: {
  task: TaskDetail;
  currentUserId: string;
  onClose: () => void;
  onOpenTask?: (taskId: string) => void;
}) {
  const t = useT();
  const updateTask = useUpdateTask(task.id);
  const assignTask = useAssignTask(task.id);
  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const moveTask = useMoveTask();

  const completed = task.status === 'COMPLETED';
  const canDelegate = task.created_by_user_id === currentUserId;
  // Handing over is not reassigning: whoever is holding the task may ask
  // somebody to check it, and the creator is told either way.
  const canHandOver =
    canDelegate || task.assigned_to_user_id === currentUserId;
  const [handingOver, setHandingOver] = useState(false);
  const today = toDayString(new Date());

  return (
    <div className="space-y-5">
      {/* What is being asked, above everything: it is the reason the task is
          on somebody's list at all, and a comment thread scrolls. */}
      <HandoverBanner
        task={task}
        currentUserId={currentUserId}
        onClose={onClose}
      />
      <div className="flex items-start gap-3">
        <TaskCheckbox
          checked={completed}
          label={task.title}
          // An open step makes completion impossible, and so does an unreleased
          // guard; disabling the box says so before the click rather than
          // after the error.
          disabled={
            !completed &&
            (task.blocked_by_subtasks || (task.close_protected && !task.close_armed))
          }
          title={
            completed
              ? undefined
              : task.blocked_by_subtasks
                ? `${t('task.blockedBySubtasks')} (${task.subtask_done}/${task.subtask_total})`
                : task.close_protected && !task.close_armed
                  ? t('task.protected.hint')
                  : undefined
          }
          onToggle={() => {
            if (completed) reopenTask.mutate(task.id);
            else {
              // The dialog is already a deliberate context — no second stage
              // here. The row it returns to stays visible, struck through, and
              // its checkbox reopens, so the way back is the row itself.
              completeTask.mutate(task.id);
              onClose();
            }
          }}
          className="mt-1"
        />
        <div className="min-w-0 flex-1">
          <ParentLink task={task} onOpenTask={onOpenTask} />
          <InlineTitle
            value={task.title}
            completed={completed}
            onSave={(title) => updateTask.mutate({ title })}
          />
        </div>
      </div>

      <DescriptionEditor
        taskId={task.id}
        value={task.description}
        onSave={(description) => updateTask.mutate({ description })}
      />

      {/* The facts about the task, in a panel of their own. The pane used to
          be one unbroken column on one background, which read as a wall; each
          part now sits on its own ground so the eye can find the edges. */}
      <dl
        className={cn(
          'grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2.5 rounded-xl',
          'border border-border bg-surface p-3 text-sm shadow-xs',
          // Two columns need ~400px; on a phone the label sits above its value.
          'max-sm:grid-cols-1 max-sm:gap-y-1 max-sm:text-[15px] touch:grid-cols-1 touch:gap-y-1 touch:text-[15px]',
        )}
      >
        <Field label={t('task.createdBy')}>
          <UserChip
            userId={task.creator.id}
            user={task.creator}
            currentUserId={currentUserId}
          />
        </Field>

        <Field label={t('handover.title')}>
          {handingOver ? (
            <HandoverDialog
              task={task}
              currentUserId={currentUserId}
              onDone={(close) => {
                setHandingOver(false);
                // The task is no longer ours to look at.
                if (close) onClose?.();
              }}
            />
          ) : canHandOver && !completed ? (
            <Button variant="secondary" size="sm" onClick={() => setHandingOver(true)}>
              <Send className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {t('handover.title')}
            </Button>
          ) : (
            <span className="text-sm text-content-subtle">—</span>
          )}
        </Field>

        <Field label={t('task.assignee')}>
          {canDelegate ? (
            <AssigneePicker
              task={task}
              currentUserId={currentUserId}
              onAssign={(userId) => assignTask.mutate(userId)}
              isPending={assignTask.isPending}
            />
          ) : (
            <UserChip
              userId={task.assignee.id}
              user={task.assignee}
              currentUserId={currentUserId}
            />
          )}
        </Field>

        <Field label={t('task.scheduled')}>
          <div className="flex flex-wrap items-center gap-2">
            <Menu>
              <MenuTrigger asChild>
                <Button variant="ghost" size="sm" className="-ml-2 gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                  {task.scheduled_date ? formatDayShort(task.scheduled_date) : 'Backlog'}
                </Button>
              </MenuTrigger>
              <MenuContent>
                <MenuLabel>{t('task.move')}</MenuLabel>
                <MoveMenuItems
                  defaultDate={task.scheduled_date ?? today}
                  onMove={(target, date) =>
                    moveTask.mutate({
                      taskId: task.id,
                      target,
                      ...(date ? { date } : {}),
                    })
                  }
                />
              </MenuContent>
            </Menu>
            {/* The postponement habit, right where the next postponement
                happens: the count sits beside the control that grows it. */}
            {task.postponed_count > 0 && task.status !== 'COMPLETED' ? (
              <span
                className={cn(
                  'flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums',
                  task.postponed_count >= 3
                    ? 'bg-danger-subtle text-danger'
                    : 'bg-priority-high-subtle text-priority-high',
                )}
              >
                <History className="h-3 w-3" aria-hidden />
                {t('task.postponed.count', { count: task.postponed_count })}
              </span>
            ) : null}
          </div>
        </Field>

        <Field label={t('projects.one')}>
          <ProjectPicker task={task} />
        </Field>

        <Field label={t('clients.client')}>
          {task.parent_task_id ? (
            <span className="text-sm text-content-subtle">
              {task.client_name ?? t('clients.none')}
              {task.client_name ? ` · ${t('clients.inherited')}` : ''}
            </span>
          ) : (
            <ClientPicker task={task} />
          )}
        </Field>

        {/* Only the creator: the price the work is sold at is theirs, which
            is why the server sends it to nobody else either. */}
        {task.created_by_user_id === currentUserId ? (
          <Field label={t('billing.fixedPrice')}>
            <FixedPriceField task={task} />
          </Field>
        ) : null}

        <Field label={t('time.title')}>
          <TimeSection task={task} />
        </Field>

        <Field label={t('label.plural')}>
          <div className="-ml-1">
            <LabelPicker taskId={task.id} selected={task.labels} />
          </div>
        </Field>

        <Field label="Priorita">
          <Menu>
            <MenuTrigger asChild>
              <Button variant="ghost" size="sm" className="-ml-2 gap-2">
                <PriorityDot priority={task.priority} />
                {t(PRIORITY_LABELS[task.priority])}
              </Button>
            </MenuTrigger>
            <MenuContent>
              {PRIORITY_ORDER.map((priority) => (
                <MenuItem
                  key={priority}
                  onSelect={() => updateTask.mutate({ priority })}
                  className={priority === task.priority ? 'bg-surface-hover' : ''}
                >
                  <PriorityDot priority={priority} />
                  {t(PRIORITY_LABELS[priority])}
                </MenuItem>
              ))}
            </MenuContent>
          </Menu>
        </Field>

        <Field label={t('task.createdAt')}>
          <span className="text-content-muted">{formatTimestamp(task.created_at)}</span>
        </Field>

        {task.completed_at ? (
          <Field label={t('task.completedAt')}>
            <span className="text-content-muted">
              {formatTimestamp(task.completed_at)}
              {task.completed_by ? ` · @${task.completed_by.username}` : ''}
            </span>
          </Field>
        ) : null}
      </dl>

      {/* Where this task sits: a step of something, or a task in its own
          right that could become one. */}
      <ParentPicker task={task} />

      <Subtasks task={task} onOpenSubtask={onOpenTask} />

      <CloseGuard task={task} />

      <NotesStrip taskId={task.id} />
      <Attachments taskId={task.id} />
      <CommentsSection taskId={task.id} />
      <ActivitySection taskId={task.id} />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs uppercase tracking-wide text-content-subtle max-sm:mt-2.5 max-sm:first:mt-0 touch:mt-2.5 touch:first:mt-0">
        {label}
      </dt>
      <dd className="min-w-0 max-sm:min-h-8 touch:min-h-8">{children}</dd>
    </>
  );
}

function UserChip({
  user,
  currentUserId,
}: {
  userId: string;
  user: {
    id: string;
    username: string;
    display_name: string;
    avatar_url: string | null;
  };
  currentUserId: string;
}) {
  const t = useT();
  return (
    <span className="flex items-center gap-2">
      <Avatar user={user} size="md" />
      <span className="text-content">
        {user.id === currentUserId ? t('common.me') : user.display_name}
      </span>
      <span className="text-content-subtle">@{user.username}</span>
    </span>
  );
}

/**
 * Inline title editing (spec §14).
 *
 * Click to edit, Enter or blur to save, Escape to abandon. No edit button and
 * no separate save step — the point is to change a word without ceremony.
 */
function InlineTitle({
  value,
  completed,
  onSave,
}: {
  value: string;
  completed: boolean;
  onSave: (title: string) => void;
}) {
  // `null` means "not editing". Holding both in one piece of state means the
  // draft is seeded from the current value at the moment editing starts, with
  // no effect syncing a prop into state on every render.
  const [draft, setDraft] = useState<string | null>(null);

  function commit() {
    const next = (draft ?? '').trim();
    setDraft(null);
    if (next && next !== value) onSave(next);
  }

  if (draft === null) {
    return (
      <button
        type="button"
        onClick={() => setDraft(value)}
        className={cn(
          'flex-1 rounded px-1 py-0.5 text-left text-lg font-medium leading-snug',
          'transition-surface hover:bg-surface-hover',
          completed ? 'text-content-subtle line-through' : 'text-content',
        )}
      >
        {value}
      </button>
    );
  }

  return (
    <input
      autoFocus
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(null);
        }
      }}
      className={cn(
        'flex-1 rounded border border-accent bg-surface px-1 py-0.5',
        'text-lg font-medium leading-snug text-content outline-none',
      )}
    />
  );
}

/** Delegation from the detail view — creator only, and always by user id. */
function AssigneePicker({
  task,
  currentUserId,
  onAssign,
  isPending,
}: {
  task: TaskDetail;
  currentUserId: string;
  onAssign: (userId: string) => void;
  isPending: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={isPending}
        className="-ml-1 flex items-center gap-2 rounded px-1 py-0.5 transition-surface hover:bg-surface-hover"
      >
        <Avatar user={task.assignee} size="md" />
        <span className="text-content">
          {task.assignee.id === currentUserId
            ? t('common.me')
            : task.assignee.display_name}
        </span>
        <span className="text-content-subtle">@{task.assignee.username}</span>
        <UserPlus className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
      </button>
    );
  }

  // Mounted only while open, so the field starts at '@' every time without an
  // effect to reset it.
  return (
    <AssigneeSearch
      onAssign={(userId) => {
        onAssign(userId);
        setOpen(false);
      }}
      onCancel={() => setOpen(false)}
    />
  );
}

function AssigneeSearch({
  onAssign,
  onCancel,
}: {
  onAssign: (userId: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const { field, inputRef } = useMentionInput('@');

  return (
    <div className="relative">
      <input
        ref={inputRef}
        autoFocus
        value={field.value}
        onChange={(event) =>
          field.handleChange(event.target.value, event.target.selectionStart ?? 0)
        }
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !field.mention) onCancel();
        }}
        onBlur={() => {
          if (!field.mention) onCancel();
        }}
        placeholder={t('task.mentionPlaceholder')}
        aria-label={t('task.delegateTo')}
        className="w-56 rounded border border-accent bg-surface px-2 py-1 text-sm outline-none"
      />
      {field.mention ? (
        <MentionPicker
          query={field.mention.query}
          onDismiss={field.dismissMention}
          inputRef={inputRef}
          onSelect={(user) => onAssign(user.id)}
        />
      ) : null}
    </div>
  );
}

function CommentsSection({ taskId }: { taskId: string }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const { data: comments } = useQuery({
    queryKey: keys.comments(taskId),
    queryFn: () => commentsApi.list(taskId),
  });

  async function submit() {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await commentsApi.create(taskId, body);
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: keys.comments(taskId) });
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
    } finally {
      setSending(false);
    }
  }

  return (
    <section className="border-t border-border pt-4">
      <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-content-subtle">
        {t('comment.plural')}
      </h3>

      <ul className="mb-3 space-y-3">
        {(comments ?? []).map((comment) => (
          <li key={comment.id} className="flex gap-2.5">
            <Avatar user={comment.author} size="md" className="mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="flex items-baseline gap-2">
                <span className="text-sm font-medium text-content">
                  {comment.author.display_name}
                </span>
                <span className="text-xs text-content-subtle">
                  {formatTimestamp(comment.created_at)}
                </span>
              </p>
              <Markdown source={comment.body} />
            </div>
          </li>
        ))}
      </ul>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        className="flex items-start gap-2"
      >
        <Textarea
          rows={2}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void submit();
            }
          }}
          placeholder={t('comment.placeholder')}
          className="text-sm max-sm:text-[15px] touch:text-[15px]"
        />
        <Button
          type="submit"
          variant="secondary"
          size="icon"
          disabled={!draft.trim() || sending}
          aria-label={t('comment.send')}
        >
          <Send className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </form>
    </section>
  );
}

/**
 * Knowledge-base notes that mention this task.
 *
 * The other half of `[title](/tasks/…)` in a note: the task shows which notes
 * talk about it. Both visibilities are enforced server-side — a colleague's
 * private note never appears here just because it mentions a shared task.
 */
function NotesStrip({ taskId }: { taskId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const { data: notes } = useQuery({
    queryKey: ['kb', 'forTask', taskId],
    queryFn: () => kb.notesForTask(taskId),
    staleTime: 30_000,
  });

  if (!notes || notes.length === 0) return null;

  return (
    <section className="border-t border-border pt-4">
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-content-subtle">
        <BookOpen className="h-3.5 w-3.5" aria-hidden />
        {t('kb.notesOnTask')}
      </h3>
      <ul className="space-y-1">
        {notes.map((note) => (
          <li key={note.id}>
            <button
              type="button"
              onClick={() => navigate(`/kb/notes/${note.id}`)}
              className="flex items-center gap-1.5 text-sm text-accent underline-offset-2 hover:underline"
            >
              {note.is_sensitive ? <Lock className="h-3 w-3" aria-hidden /> : null}
              {note.title}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The audit trail (spec §26).
 *
 * This is what makes a delegated task accountable: the creator can see that the
 * assignee rescheduled it, and when they completed it.
 */
function ActivitySection({ taskId }: { taskId: string }) {
  const t = useT();
  const { data } = useQuery({
    queryKey: keys.activity(taskId),
    queryFn: () => activityApi.list(taskId, { limit: 50 }),
  });

  const events = data?.items ?? [];
  if (events.length === 0) return null;

  return (
    <section className="border-t border-border pt-4">
      <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wide text-content-subtle">
        {t('activity.title')}
      </h3>
      <ul className="space-y-1.5">
        {events.map((event) => (
          <li key={event.id} className="flex gap-2 text-xs text-content-subtle">
            <Clock className="mt-0.5 h-3 w-3 shrink-0 opacity-60" aria-hidden />
            <span className="tabular-nums">{formatTimestamp(event.created_at)}</span>
            <span className="text-content-muted">{describeEvent(event, t)}</span>
            <span className="truncate">
              · {event.actor?.display_name ?? t('common.system')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Turn an audit row into a line of history.
 *
 * The actor is rendered separately rather than folded into a sentence. Czech
 * and German inflect the past tense by gender — "vytvořil" / "vytvořila" — and
 * we do not know anyone's, so "Task created · Miroslav" is both correct in
 * every language and easier to scan down a column than a wall of sentences.
 *
 * Reads the denormalised handles out of `metadata` rather than joining to the
 * current user record on purpose — an audit line must still read correctly
 * years later, even if a handle has since changed hands.
 */
function describeEvent(
  event: Activity,
  t: (key: MessageKey, params?: Params) => string,
): string {
  const meta = event.metadata as Record<string, string | null | undefined>;

  // The log stores ISO days; the rest of the UI speaks the reader's locale, and
  // the history should not be the one place that leaks the storage format.
  const day = (value: string | null | undefined) =>
    value ? formatDayShort(value) : t('nav.backlog');

  switch (event.event_type) {
    case 'TASK_CREATED':
      return t('activity.created');
    case 'ASSIGNED':
      return t('activity.assigned', { to: meta.to_username ?? '?' });
    case 'REASSIGNED':
      return t('activity.reassigned', {
        from: meta.from_username ?? '?',
        to: meta.to_username ?? '?',
      });
    case 'HANDED_OVER':
      // The kind is the point of the line: "handed to Petr" and "asked Petr
      // to check it" are different events in a history somebody is reading
      // to work out what happened.
      return t('activity.handedOver', {
        to: meta.to_username ?? '?',
        kind: t(`handover.kind.${(meta.kind ?? 'DO') as HandoverKind}` as MessageKey),
      });
    case 'HANDED_BACK':
      return t('activity.handedBack', { to: meta.to_username ?? '?' });
    case 'SCHEDULED':
      return t('activity.scheduled', { to: day(meta.to) });
    case 'RESCHEDULED':
      return t('activity.rescheduled', { from: day(meta.from), to: day(meta.to) });
    case 'MOVED_TO_BACKLOG':
      return t('activity.movedToBacklog');
    case 'PRIORITY_CHANGED':
      return t('activity.priorityChanged', {
        to: t(PRIORITY_LABELS[(meta.to ?? 'NONE') as TaskPriority]),
      });
    case 'STATUS_CHANGED':
      return t('activity.statusChanged', { to: meta.to ?? '?' });
    case 'COMPLETED':
      return t('activity.completed');
    case 'REOPENED':
      return t('activity.reopened');
    case 'CANCELLED':
      return t('activity.cancelled');
    case 'ATTACHMENT_ADDED':
      return t('activity.attachmentAdded', { name: meta.filename ?? '' }).trim();
    case 'ATTACHMENT_REMOVED':
      return t('activity.attachmentRemoved', { name: meta.filename ?? '' }).trim();
    case 'COMMENT_ADDED':
      return t('activity.commentAdded');
    case 'LABELS_CHANGED': {
      const to = Array.isArray(event.metadata.to) ? event.metadata.to : [];
      return to.length > 0
        ? t('activity.labelsSet', { labels: to.join(', ') })
        : t('activity.labelsCleared');
    }
    case 'SUBTASK_ADDED':
      return t('activity.subtaskAdded', { title: meta.title ?? '?' });
    case 'SUBTASK_REMOVED':
      return t('activity.subtaskRemoved', { title: meta.title ?? '?' });
    case 'TASK_UPDATED':
      return t('activity.updated');
    case 'TASK_DELETED':
      return t('activity.deleted');
    case 'TASK_RESTORED':
      return t('activity.restored');
    default:
      return event.event_type;
  }
}


/**
 * Which client a project bills to — a plain select, because the list is a
 * handful of names, not a search problem.
 */
function ClientPicker({ task }: { task: TaskDetail }) {
  const t = useT();
  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => billingApi.clients(),
  });
  const updateTask = useUpdateTask(task.id);

  return (
    <select
      value={task.client_id ?? ''}
      onChange={(event) => updateTask.mutate({ client_id: event.target.value || null })}
      className="-ml-1 rounded-lg bg-transparent px-1 py-1 text-sm text-content hover:bg-surface-hover focus:outline-none"
    >
      <option value="">{t('clients.none')}</option>
      {(clients ?? []).map((row) => (
        <option key={row.id} value={row.id}>
          {row.name}
        </option>
      ))}
    </select>
  );
}


const STATE_TONE: Record<FixedChargeState, string> = {
  AGREED: 'bg-surface-sunken text-content-muted',
  UNBILLED: 'bg-accent/10 text-accent',
  OWED: 'bg-amber/10 text-amber',
  PAID: 'bg-success-subtle text-success',
  WRITTEN_OFF: 'bg-surface-sunken text-content-subtle',
};

/**
 * A price for the job instead of a rate for its hours.
 *
 * Repairs are quoted this way, so the field has to be as easy as typing the
 * number — but it is also the one number that decides an invoice, so it says
 * out loud what it does to the hours, and refuses to move once a protocol
 * carries it. The state chip is the answer to "did they pay for that one?".
 */
function FixedPriceField({ task }: { task: TaskDetail }) {
  const t = useT();
  const updateTask = useUpdateTask(task.id);
  const [draft, setDraft] = useState(task.fixed_amount ?? '');
  const [saved, setSaved] = useState(task.fixed_amount);
  const billed =
    task.fixed_charge_state === 'OWED' ||
    task.fixed_charge_state === 'PAID' ||
    task.fixed_charge_state === 'WRITTEN_OFF';

  // The server changed the price under us (a save landed, or another window
  // did it): take the new value. Adjusted during render rather than in an
  // effect, which would re-render the whole detail a second time for a string.
  if (saved !== task.fixed_amount) {
    setSaved(task.fixed_amount);
    setDraft(task.fixed_amount ?? '');
  }

  const commit = () => {
    const next = draft.trim().replace(',', '.');
    if (next === (task.fixed_amount ?? '')) return;
    if (next !== '' && !/^\d+(\.\d{1,2})?$/.test(next)) {
      setDraft(task.fixed_amount ?? '');
      return;
    }
    updateTask.mutate({ fixed_amount: next === '' ? null : next });
  };

  if (billed) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm tabular-nums text-content">
          {formatAmount(Number(task.fixed_amount ?? 0), 'CZK')}
        </span>
        <span className="text-xs text-content-muted">
          {t(task.fixed_amount_with_time ? 'billing.mode.with' : 'billing.mode.instead')}
        </span>
        <StateChip state={task.fixed_charge_state!} />
        <span className="basis-full text-[11px] text-content-subtle">
          {task.fixed_charge_report_number
            ? t('billing.billedCannotEdit', { number: task.fixed_charge_report_number })
            : t('billing.settledCannotEdit')}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={draft}
        inputMode="decimal"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') setDraft(task.fixed_amount ?? '');
        }}
        placeholder={t('billing.fixedPricePlaceholder')}
        aria-label={t('billing.fixedPrice')}
        className="w-28 rounded-lg bg-surface px-2 py-1 text-sm tabular-nums text-content ring-1 ring-border placeholder:text-content-subtle focus:outline-none focus:ring-accent max-sm:min-h-10 touch:min-h-10"
      />
      {task.fixed_charge_state ? (
        <StateChip state={task.fixed_charge_state} />
      ) : (
        <span className="text-xs text-content-subtle">{t('billing.perHour')}</span>
      )}

      {/* What the amount does to the tracked hours. Only once there is an
          amount for it to be true of — and the server refuses the pair
          otherwise. A quoted repair replaces the hours; a part or a licence
          is billed on top of them. */}
      {task.fixed_amount ? (
        <div className="basis-full">
          <div className="inline-flex rounded-lg bg-surface-sunken p-0.5 text-xs">
            {([false, true] as const).map((withTime) => (
              <button
                key={String(withTime)}
                type="button"
                aria-pressed={task.fixed_amount_with_time === withTime}
                onClick={() =>
                  task.fixed_amount_with_time === withTime
                    ? undefined
                    : updateTask.mutate({ fixed_amount_with_time: withTime })
                }
                className={cn(
                  'rounded-md px-2 py-1 transition-surface max-sm:min-h-9 touch:min-h-9',
                  task.fixed_amount_with_time === withTime
                    ? 'bg-surface font-medium text-content shadow-sm'
                    : 'text-content-muted hover:text-content',
                )}
              >
                {t(withTime ? 'billing.mode.with' : 'billing.mode.instead')}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <span className="basis-full text-[11px] text-content-subtle">
        {!task.fixed_amount
          ? t('billing.fixedPriceHint')
          : `${t(
              task.fixed_amount_with_time
                ? 'billing.mode.withHint'
                : 'billing.mode.insteadHint',
            )} ${
              task.fixed_charge_state
                ? t(`billing.hint.${task.fixed_charge_state}` as MessageKey)
                : ''
            }`.trim()}
      </span>
    </div>
  );
}

export function StateChip({ state }: { state: FixedChargeState }) {
  const t = useT();
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-[11px] font-medium',
        STATE_TONE[state],
      )}
    >
      {t(`billing.state.${state}` as MessageKey)}
    </span>
  );
}


/** Which project a task belongs to — at most one, so a plain select. */
function ProjectPicker({ task }: { task: TaskDetail }) {
  const t = useT();
  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
  });
  const updateTask = useUpdateTask(task.id);

  return (
    <select
      value={task.project_id ?? ''}
      onChange={(event) => updateTask.mutate({ project_id: event.target.value || null })}
      className="-ml-1 rounded-lg bg-transparent px-1 py-1 text-sm text-content hover:bg-surface-hover focus:outline-none"
    >
      <option value="">{t('projects.none')}</option>
      {(projects ?? []).map((project) => (
        <option key={project.id} value={project.id}>
          {project.name}
        </option>
      ))}
    </select>
  );
}
